import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAnalytics, getAnalyticsSummary, getOrders, isAuthenticated } from '../../services/adminService';
import { getCached, setCached } from '../../utils/adminCache';
import AdminSidebar from '../../components/AdminSidebar';
import OrdersTable, {
  orderIdColumn, customerColumn, roleColumn, industryColumn, stageColumn,
  receivedColumn, lastUpdateColumn,
} from '../../components/admin/OrdersTable';

// ── Color tokens ─────────────────────────────────────────────────────────────
const T = {
  navy: '#0D1F3C', primary: '#1A6FD6', primaryLight: '#EFF6FF',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  purple: '#8B5CF6', cyan: '#06B6D4',
  text: '#0F172A', muted: '#64748B', faint: '#94A3B8',
  border: '#E2E8F0', borderLight: '#F1F5F9', bg: '#FFFBEB', white: '#fff',
};

// ── Stage config ─────────────────────────────────────────────────────────────
const STAGES = {
  new:               { label: 'New',               color: T.warning,  bg: '#FEF3C7' },
  intake_pending:    { label: 'Intake Pending',     color: T.warning,  bg: '#FEF3C7' },
  intake_done:       { label: 'Intake Done',        color: '#D97706',  bg: '#FEF9C3' },
  recruiter_assigned:{ label: 'Assigned',           color: T.primary,  bg: '#DBEAFE' },
  expert_review:     { label: 'Expert Review',      color: T.purple,   bg: '#EDE9FE' },
  ai_rewrite:        { label: 'AI Rewrite',         color: T.purple,   bg: '#EDE9FE' },
  expert_qa:         { label: 'Expert QA',          color: T.cyan,     bg: '#CFFAFE' },
  recruiter_cert:    { label: 'Recruiter Cert',     color: '#0E7490',  bg: '#CFFAFE' },
  final_fix:         { label: 'Final Fix',          color: T.danger,   bg: '#FEE2E2' },
  delivered:         { label: 'Delivered',          color: T.success,  bg: '#DCFCE7' },
};
const getStageCfg = s => STAGES[s] || { label: s || 'Unknown', color: T.faint, bg: '#F1F5F9' };

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatAgo = d => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d);
  const m = Math.floor(ms / 60000), h = Math.floor(m / 60), dy = Math.floor(h / 24);
  if (m < 60) return `${m}m ago`; if (h < 24) return `${h}h ago`; return `${dy}d ago`;
};
const formatDate = iso => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
};

// ── Stage badge ──────────────────────────────────────────────────────────────
const StagePill = ({ stage }) => {
  const { label, color, bg } = getStageCfg(stage);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
      background: bg, color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
};

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accentColor, icon }) => (
  <div style={{
    background: T.white, borderRadius: 14, border: `1px solid ${T.border}`,
    padding: '20px 24px', flex: 1, minWidth: 150,
    borderTop: accentColor ? `3px solid ${accentColor}` : `1px solid ${T.border}`,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: T.faint }}>{label}</div>
      {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
    </div>
    <div style={{ fontSize: 30, fontWeight: 800, color: accentColor || T.navy, lineHeight: 1, letterSpacing: '-1px' }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{sub}</div>}
  </div>
);

// ── CEO-quality SVG bar chart ────────────────────────────────────────────────
const BarChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);
  if (!data.length) return (
    <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: 13, flexDirection: 'column', gap: 8 }}>
      <svg width="40" height="40" fill="none" viewBox="0 0 40 40">
        <rect x="4" y="20" width="8" height="16" rx="2" fill="#E2E8F0"/>
        <rect x="16" y="12" width="8" height="24" rx="2" fill="#CBD5E1"/>
        <rect x="28" y="6" width="8" height="30" rx="2" fill="#94A3B8"/>
      </svg>
      No data yet
    </div>
  );

  const W = 720, H = 220;
  const ml = 44, mr = 12, mt = 28, mb = 36;
  const chartW = W - ml - mr;
  const chartH = H - mt - mb;

  const max = Math.max(...data.map(d => d.count), 1);
  const ticks = [0, Math.ceil(max / 2), max];
  const barW = Math.max(6, Math.min(36, chartW / data.length - 4));
  const gap = (chartW - barW * data.length) / (data.length + 1);

  const yPos = v => mt + chartH - (v / max) * chartH;

  // Show every Nth label so they don't overlap
  const labelEvery = Math.ceil(data.length / 14);

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6"/>
            <stop offset="100%" stopColor="#1A6FD6"/>
          </linearGradient>
          <linearGradient id="barGradHover" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60A5FA"/>
            <stop offset="100%" stopColor="#2563EB"/>
          </linearGradient>
        </defs>

        {/* Grid lines + Y-axis ticks */}
        {ticks.map(v => {
          const y = yPos(v);
          return (
            <g key={v}>
              <line x1={ml} y1={y} x2={W - mr} y2={y}
                stroke={v === 0 ? '#CBD5E1' : '#F1F5F9'} strokeWidth={v === 0 ? 1 : 1}
                strokeDasharray={v === 0 ? '0' : '4 4'}/>
              <text x={ml - 8} y={y + 4} textAnchor="end" fontSize="11" fill={T.faint} fontFamily="system-ui">{v}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = ml + gap + i * (barW + gap);
          const barH2 = Math.max(d.count > 0 ? 3 : 0, (d.count / max) * chartH);
          const y = yPos(d.count);
          const label = d.date ? d.date.slice(5) : d.label || '';
          const isHovered = tooltip?.i === i;
          return (
            <g key={i} style={{ cursor: 'pointer' }}
              onMouseEnter={() => setTooltip({ i, label, count: d.count, x: x + barW / 2, y })}
              onMouseLeave={() => setTooltip(null)}>
              {/* Bar shadow */}
              <rect x={x + 1} y={y + 2} width={barW} height={barH2}
                rx="3" fill="rgba(26,111,214,0.12)"/>
              {/* Bar */}
              <rect x={x} y={y} width={barW} height={barH2}
                rx="3" fill={isHovered ? 'url(#barGradHover)' : 'url(#barGrad)'}
                style={{ transition: 'fill 0.12s' }}/>
              {/* Value label above bar */}
              {d.count > 0 && (
                <text x={x + barW / 2} y={y - 5} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill={T.primary} fontFamily="system-ui">
                  {d.count}
                </text>
              )}
              {/* X-axis date label */}
              {i % labelEvery === 0 && (
                <text
                  x={x + barW / 2} y={H - 4}
                  textAnchor="middle" fontSize="10" fill={T.faint} fontFamily="system-ui">
                  {label}
                </text>
              )}
            </g>
          );
        })}

        {/* X-axis baseline */}
        <line x1={ml} y1={mt + chartH} x2={W - mr} y2={mt + chartH} stroke="#CBD5E1" strokeWidth="1"/>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute', pointerEvents: 'none',
          left: `${(tooltip.x / W) * 100}%`, top: `${((tooltip.y - 28) / H) * 100}%`,
          transform: 'translate(-50%, -100%)',
          background: T.navy, color: T.white, borderRadius: 8, padding: '7px 12px',
          fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
          <div style={{ color: '#94A3B8', fontSize: 11, marginBottom: 2 }}>{tooltip.label}</div>
          <div style={{ fontSize: 16 }}>{tooltip.count} orders</div>
          <div style={{
            position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: `5px solid ${T.navy}`,
          }}/>
        </div>
      )}
    </div>
  );
};

// ── Donut chart ──────────────────────────────────────────────────────────────
const DonutChart = ({ segments = [] }) => {
  const [hovered, setHovered] = useState(null);
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const r = 58, r2 = 38, cx = 70, cy = 70;
  let cumAngle = -Math.PI / 2;

  const arc = (r, start, end) => {
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <svg width="140" height="140" viewBox="0 0 140 140" style={{ flexShrink: 0 }}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth="20"/>
        {segments.map((seg, i) => {
          const angle = (seg.value / total) * 2 * Math.PI;
          const startA = cumAngle, endA = cumAngle + angle;
          cumAngle = endA;
          const isH = hovered === i;
          const rr = isH ? r + 4 : r;
          return (
            <path key={i}
              d={`${arc(rr, startA, endA)} L ${cx + r2 * Math.cos(endA)} ${cy + r2 * Math.sin(endA)} A ${r2} ${r2} 0 ${angle > Math.PI ? 1 : 0} 0 ${cx + r2 * Math.cos(startA)} ${cy + r2 * Math.sin(startA)} Z`}
              fill={seg.color} opacity={hovered !== null && !isH ? 0.6 : 1}
              style={{ transition: 'all 0.2s', cursor: 'pointer' }}
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
            />
          );
        })}
        {/* Center */}
        <circle cx={cx} cy={cy} r={r2 - 2} fill="white"/>
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="20" fontWeight="800" fill={T.navy} fontFamily="system-ui">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill={T.faint} fontFamily="system-ui">orders</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {segments.map((seg, i) => {
          const pct = Math.round((seg.value / total) * 100);
          return (
            <div key={i} style={{ cursor: 'pointer', opacity: hovered !== null && hovered !== i ? 0.5 : 1, transition: 'opacity 0.15s' }}
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: T.muted }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: seg.color, flexShrink: 0 }}/>
                  {seg.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{seg.value}</span>
                  <span style={{ fontSize: 10, color: T.faint }}>{pct}%</span>
                </div>
              </div>
              <div style={{ height: 4, background: '#F1F5F9', borderRadius: 99 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: seg.color, borderRadius: 99, transition: 'width 0.5s ease' }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Inline OrdersTable extracted to /components/admin/OrdersTable.jsx — used below.

// ── Main component ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState({ buckets: {}, counts: {} });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const CACHE_KEY = 'dashboard:v1';

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    // Stale-while-revalidate: if we've already loaded the dashboard once
    // this session, paint that instantly (no spinner) and refresh quietly
    // in the background. Only a genuinely first visit blocks on the network.
    const cached = getCached(CACHE_KEY);
    if (cached) {
      setAnalytics(cached.analytics);
      setSummary(cached.summary);
      setOrders(cached.orders);
      setLastRefresh(cached.fetchedAt);
      setLoading(false);
      loadAll(false);
    } else {
      loadAll(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadAll = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    // Use Promise.allSettled so a failure in ONE endpoint (e.g. /analytics
    // returns 500) doesn't blank out the entire dashboard. Each result is
    // applied independently — failed ones just stay at their default state.
    const [aRes, sRes, oRes] = await Promise.allSettled([
      getAnalytics(),
      getAnalyticsSummary(),
      getOrders(),
    ]);
    const next = { analytics: null, summary: null, orders: { buckets: {}, counts: {} } };
    if (aRes.status === 'fulfilled') next.analytics = aRes.value;
    else console.error('Analytics fetch failed:', aRes.reason);
    if (sRes.status === 'fulfilled') next.summary = sRes.value;
    else console.error('Analytics summary fetch failed:', sRes.reason);
    if (oRes.status === 'fulfilled') next.orders = oRes.value;
    else console.error('Orders fetch failed:', oRes.reason);
    setAnalytics(next.analytics);
    setSummary(next.summary);
    setOrders(next.orders);
    const fetchedAt = new Date();
    setLastRefresh(fetchedAt);
    setLoading(false);
    setCached(CACHE_KEY, { ...next, fetchedAt });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg }}>
      <AdminSidebar/>
      <div style={{ marginLeft: 224, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: T.faint }}>
          <div style={{ width: 36, height: 36, border: `3px solid #E2E8F0`, borderTopColor: T.primary, borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }}/>
          <div style={{ fontSize: 14 }}>Loading dashboard…</div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const newCount    = orders.counts?.new || 0;
  const inProgCount = orders.counts?.in_progress || 0;
  const overdue     = orders.counts?.overdue || 0;
  const completed   = orders.counts?.completed || 0;

  // Flat list of all orders for table view
  const allOrders = Object.values(orders.buckets || {}).flat();

  const donutData = [
    { label: 'New / Intake', value: newCount,    color: T.warning  },
    { label: 'In Progress',  value: inProgCount, color: T.purple   },
    { label: 'Overdue',      value: overdue,     color: T.danger   },
    { label: 'Completed',    value: completed,   color: T.success  },
  ].filter(s => s.value > 0);

  const breakdownRows = [
    { label: 'New / Intake',          key: 'new',                  color: T.warning  },
    { label: 'In Progress',           key: 'in_progress',          color: T.purple   },
    { label: 'Recruiter Responded',   key: 'recruiter_responded',  color: T.primary  },
    { label: 'Overdue',               key: 'overdue',              color: T.danger   },
    { label: 'Delivered',             key: 'completed',            color: T.success  },
  ];
  const grandTotal = breakdownRows.reduce((s, r) => s + (orders.counts?.[r.key] || 0), 0) || 1;

  const timeStr = lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <AdminSidebar/>

      <main style={{ marginLeft: 224, flex: 1, padding: '28px 32px', minWidth: 0 }}>
        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.navy, letterSpacing: '-0.5px' }}>Order Analytics</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: T.faint }}>Live stats across all orders · Refreshed at {timeStr}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: 10, padding: 3 }}>
              {[['table', '☰ Table'], ['chart', '📊 Charts']].map(([m, lbl]) => (
                <button key={m} onClick={() => setViewMode(m)} style={{
                  padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13,
                  background: viewMode === m ? T.white : 'transparent',
                  color: viewMode === m ? T.navy : T.muted,
                  boxShadow: viewMode === m ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
                  transition: 'all .15s',
                }}>{lbl}</button>
              ))}
            </div>
            <button onClick={loadAll} style={{ padding: '9px 18px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 16 16">
                <path d="M13.5 8A5.5 5.5 0 112.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M13.5 4v4h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* ── 6 Core stat cards (Total Revenue + 5 ops metrics) ── */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
          <Link to="/admin/revenue" style={{ flex: '1 1 180px', minWidth: 180, textDecoration: 'none' }}>
            <StatCard
              label="Total Revenue"
              value={`₹${Number(analytics?.total_revenue || 0).toLocaleString('en-IN')}`}
              sub={`₹${Number(analytics?.revenue_this_month || 0).toLocaleString('en-IN')} this month · ${analytics?.paid_orders_count || 0} paid`}
              accentColor={T.success}
            />
          </Link>
          <StatCard label="Total Orders" value={analytics?.total_orders || allOrders.length || 0} sub={`${analytics?.orders_this_month ?? 0} this month`} accentColor={T.primary}/>
          <StatCard label="Delivered"    value={analytics?.delivered || completed} sub={`${analytics?.completion_rate || 0}% completion rate`} accentColor={T.success}/>
          <StatCard label="Avg Turnaround" value={`${analytics?.avg_turnaround_hours || 0}h`} sub="Mean time to delivery" accentColor={T.cyan}/>
          <StatCard label="In Progress"  value={inProgCount} sub={overdue > 0 ? `⚠ ${overdue} overdue` : 'Active in pipeline'} accentColor={overdue > 0 ? T.danger : T.purple}/>
          <StatCard label="Recruiter Response" value={`${analytics?.recruiter_response_rate || 0}%`} sub="Within 48 hours" accentColor={T.warning}/>
        </div>

        {/* ── Secondary metrics ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {/* 'Landing Clicks' used to sit here reading summary.landing_clicks,
              which came from click_logs — a table nothing has ever written to,
              so the tile was permanently 0. Replaced with the evaluation count,
              which is both real and the actual top of the funnel. Full funnel
              breakdown lives in the Analytics tab.
              'Cached Profiles' was also a copy/paste of linkdapi_calls, showing
              the identical number twice; it now reports unique evaluated users. */}
          {[
            { label: 'Evaluations',  value: summary?.evaluations_total ?? '—', icon: '🔍', desc: 'Free evaluations run' },
            { label: 'API Calls',    value: summary?.linkdapi_calls ?? '—',  icon: '🔗', desc: 'LinkedIn profiles scraped' },
            { label: 'Unique Users', value: summary?.unique_whatsapp_users ?? '—', icon: '📱', desc: 'By WhatsApp number' },
            { label: 'Orders',       value: summary?.table_counts?.orders ?? '—', icon: '🧾', desc: 'All-time paid orders' },
          ].map(m => (
            <div key={m.label} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px' }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{m.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: T.faint, marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: '-0.5px' }}>{m.value}</div>
              <div style={{ fontSize: 11, color: T.faint, marginTop: 3 }}>{m.desc}</div>
            </div>
          ))}
        </div>

        {/* ── Status Breakdown ── */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 24, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>Status Breakdown</span>
            <span style={{ fontSize: 12, color: T.faint }}>{grandTotal} orders total</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {breakdownRows.map(row => {
              const count = orders.counts?.[row.key] || 0;
              const pct = Math.round((count / grandTotal) * 100);
              return (
                <div key={row.key} style={{ display: 'grid', gridTemplateColumns: '180px 60px 1fr 60px', alignItems: 'center', padding: '10px 20px', gap: 16, borderBottom: `1px solid ${T.borderLight}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: T.text }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: row.color, flexShrink: 0 }}/>
                    {row.label}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: row.color }}>{count}</div>
                  <div style={{ height: 8, background: '#F1F5F9', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: row.color, borderRadius: 99, transition: 'width 0.6s ease' }}/>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.muted, textAlign: 'right' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Table or Chart view ── */}
        {viewMode === 'chart' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Bar chart */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '24px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.navy }}>Daily Order Volume</div>
                  <div style={{ fontSize: 12, color: T.faint, marginTop: 2 }}>Last 30 days</div>
                </div>
                <div style={{ fontSize: 11, color: T.faint, background: T.primaryLight, padding: '4px 10px', borderRadius: 20, fontWeight: 600 }}>
                  {(summary?.orders_per_day || []).reduce((s, d) => s + d.count, 0)} total
                </div>
              </div>
              <BarChart data={summary?.orders_per_day || []}/>
            </div>

            {/* Donut chart */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: T.navy, marginBottom: 4 }}>Pipeline Distribution</div>
              <div style={{ fontSize: 12, color: T.faint, marginBottom: 20 }}>Current order stages</div>
              <DonutChart segments={donutData}/>
            </div>

            {/* DB table sizes */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24, gridColumn: '1 / -1' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: T.navy, marginBottom: 16 }}>Database Table Sizes</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                {Object.entries(summary?.table_counts || {}).map(([tbl, cnt]) => (
                  <div key={tbl} style={{ background: T.bg, borderRadius: 10, padding: '12px 16px', border: `1px solid ${T.borderLight}` }}>
                    <div style={{ fontSize: 10, color: T.faint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{tbl.replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.navy, letterSpacing: '-0.5px' }}>{cnt ?? '—'}</div>
                    <div style={{ fontSize: 10, color: T.faint, marginTop: 2 }}>rows</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ── TABLE VIEW → LINK TO UNIFIED ORDERS PAGE ── */
          <div style={{
            background: 'white', borderRadius: 12, border: `1px solid ${T.border}`,
            padding: '24px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.navy }}>All Orders</h2>
              <div style={{ fontSize: 12, color: T.faint, marginTop: 4 }}>
                {allOrders.length} orders in system — manage them from the unified Orders page
              </div>
            </div>
            <a
              href="/admin/orders"
              style={{
                background: T.navy, color: 'white', textDecoration: 'none',
                padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600,
              }}
            >
              View all orders →
            </a>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
